// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Chip, FormControl, Select, MenuItem, SelectChangeEvent } from '@mui/material'

// ** Util Imports
import { produce } from 'immer'

// ** Child Imports
import { fetchFilterColumnValues } from 'src/lib/core/api-methods'
import { DataFilterColumnOptionType, DataFilterConfigType, FilterDataType } from 'src/lib/types/filters'
import DropdownButton from '../form-elements/DropdownButton'
import { Search, X, XCircle } from 'react-feather'
import { LoadingButton } from '@mui/lab'
import { apiURI } from 'src/lib/utils/constants'
import { getRequest } from 'src/lib/core/api-client'
import MultiSelectInput from 'src/components/common/form-elements/MultiSelectInput'

interface FilterFormProps {
  isQueryBasedFilter: boolean
  filterData: FilterDataType
  setFilterArrayData: any
  startDate: string
  endDate: string
  filterIndex: number
  filterArrayData: FilterDataType[]
  filterConfig: DataFilterConfigType
}

const FilterForm: React.FC<FilterFormProps> = ({
  isQueryBasedFilter,
  filterData,
  setFilterArrayData,
  startDate,
  endDate,
  filterIndex,
  filterArrayData,
  filterConfig
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [avaliableFilterOptions, setAvailableFilterOptions] = useState<DataFilterColumnOptionType[]>()
  const [selectedOptions, setSelectedOptions] = useState<string[]>(filterData.values)
  const availableOptions: any = filterData?.availableOptions?.map((item: string) => {
    return {
      value: item,
      label: item
    }
  })

  useEffect(() => {
    const modifiedFilter = { ...filterData }
    modifiedFilter.values = [...selectedOptions]
    setFilterArrayData(
      produce((draft: any) => {
        draft[filterIndex] = modifiedFilter
      })
    )
  }, [selectedOptions])

  useEffect(() => {
    if (!filterData) return
    if (filterData.values?.length) return
    Boolean(isQueryBasedFilter) && !Boolean(filterData.columnName === 'channels')
      ? handleColumnSelect(filterData.columnName)
      : fetchValuesForFilter(filterData.columnName)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let availableOptions: any = filterConfig?.columns?.filter(
      option => !filterArrayData?.some(data => data?.columnName === option?.value)
    )
    availableOptions = [
      ...[filterConfig?.columns?.find(option => option.value === filterData.columnName)],
      ...availableOptions
    ]
    setAvailableFilterOptions(availableOptions)
  }, [filterArrayData])

  const handleColumnSelect = async (columnName: string) => {
    try {
      setIsLoading(true)
      const response: any = await fetchFilterColumnValues(filterConfig?.tableName, columnName, startDate, endDate)
      if (response?.error) {
        setIsLoading(false)
        return
      }
      const modifiedFilter = { ...filterData }
      modifiedFilter.columnName = columnName
      modifiedFilter.values = []
      modifiedFilter.availableOptions = response?.data?.widgetData?.[0]?.data ?? []
      setFilterArrayData(
        produce((draft: any) => {
          draft[filterIndex] = modifiedFilter
        })
      )
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  const fetchValuesForFilter = async (columnName: string) => {
    let url
    let serviceName
    switch (columnName) {
      case 'channels':
        url = '/merchant-app/listing'
        serviceName = apiURI.AUTH_ENGINE
      default:
        url = '/merchant-app/listing'
        serviceName = apiURI.AUTH_ENGINE
    }
    try {
      setIsLoading(true)
      const response: any = await getRequest(url, serviceName)
      if (response?.error) {
        setIsLoading(false)
        return
      }

      const fetchedChannels = response?.data?.data?.map((channel: any) => {
        return {
          id: channel?.id,
          name: channel?.name
        }
      })

      const modifiedFilter = { ...filterData }
      modifiedFilter.columnName = columnName
      modifiedFilter.values = []
      modifiedFilter.availableOptions = [...fetchedChannels]
      setFilterArrayData(
        produce((draft: any) => {
          draft[filterIndex] = modifiedFilter
        })
      )
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  const handleDeleteColumnValue = (columnValue: string, filterIndex: number) => {
    const modifiedFilter = { ...filterData }
    let deletedValueIndex
    if (isQueryBasedFilter) {
      deletedValueIndex = modifiedFilter.values.findIndex((value: string) => value === columnValue)
    } else {
      deletedValueIndex = modifiedFilter.values.findIndex((value: any) => value?.id === columnValue)
    }

    const modifiedValues = [...modifiedFilter.values]
    modifiedValues.splice(deletedValueIndex, 1)
    modifiedFilter.values = modifiedValues

    setFilterArrayData(
      produce((draft: any) => {
        draft[filterIndex] = modifiedFilter
      })
    )
  }

  const handleDeleteFilter = () => {
    setFilterArrayData(
      produce((draft: any) => {
        draft.splice(filterIndex, 1)
      })
    )
  }

  return (
    <div className='flex flex-col gap-2 w-full' key={`${filterData?.columnName}`}>
      <div className='flex gap-2'>
        <DropdownButton
          fullWidth
          options={avaliableFilterOptions} //Filter the column values for not repeating the same filters.
          handleClick={(index: number) =>
            Boolean(isQueryBasedFilter) && !Boolean(avaliableFilterOptions?.[index].value === 'channels')
              ? handleColumnSelect(avaliableFilterOptions?.[index].value ?? '')
              : fetchValuesForFilter(
                  avaliableFilterOptions?.[index].value ? avaliableFilterOptions?.[index].value : 'channels'
                )
          }
          variant='outlined'
          size='large'
          selectedOption={avaliableFilterOptions?.findIndex(
            (option: DataFilterColumnOptionType, index: number) => option?.value === filterData?.columnName
          )}
        />
        <Button variant='outlined' size='small' sx={{ background: '#f0f0f0', cursor: 'text' }}>
          is
        </Button>
        {filterArrayData.length > 1 ? (
          <LoadingButton
            variant='outlined'
            size='small'
            className='bg-[#f0f0f0]'
            loadingPosition='end'
            loading={isLoading}
            onClick={handleDeleteFilter}
          >
            <XCircle className='w-4 h-4 stroke-[2.5px]' />
          </LoadingButton>
        ) : (
          <></>
        )}
      </div>
      <div>
        {isQueryBasedFilter && !Boolean(filterArrayData[filterIndex]?.columnName === 'channels') ? (
          <MultiSelectInput
            id='column-value-select'
            label=''
            values={selectedOptions}
            setValues={setSelectedOptions}
            options={availableOptions}
            placeHolderText='Search'
            allowNoSelection={true}
          />
        ) : (
          <FormControl fullWidth>
            <span className='h-0 relative flex items-center gap-2 top-5 left-2'>
              <Search className='w-4 h-4 text-[#6e6e6e] stroke-[2.5px]' /> Search
            </span>
            <Select
              size='small'
              id='column-value-select-v2'
              value=''
              onChange={(event: SelectChangeEvent) => {
                const modifiedFilter = { ...filterData }
                if (!modifiedFilter.values.includes(event.target.value)) {
                  modifiedFilter.values = [...modifiedFilter.values, event.target.value]
                }
                setFilterArrayData(
                  produce((draft: any) => {
                    draft[filterIndex] = modifiedFilter
                  })
                )
              }}
            >
              {filterData?.availableOptions?.map((option: any, index: number) => {
                return (
                  <MenuItem key={`${index}_${option}`} value={option}>
                    {option?.name}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        )}
        <div className='flex flex-wrap w-full h-full gap-2 mt-3'>
          {isQueryBasedFilter && !Boolean(filterArrayData[filterIndex]?.columnName === 'channels') ? (
            <></>
          ) : (
            filterData?.values?.map((value: any, valueIndex: number) => (
              <Chip
                key={`${valueIndex}_${value?.id}`}
                style={{
                  borderRadius: '4px',
                  border: '1px solid #F0F0F0',
                  background: '#FAFAFA',
                  fontSize: '14px',
                  color: '#6e6e6e',
                  height: '25px'
                }}
                size='medium'
                deleteIcon={<X className='stroke-[2.5px] text-[#6e6e6e]' />}
                onDelete={() => handleDeleteColumnValue(value?.id, filterIndex)}
                label={value?.name}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default FilterForm
